import type { ReactNode } from 'react';

import type { PageSummary, WorkspaceSummary } from '../api';
import { Logo } from '../components/Logo';
import { t } from '../i18n';
import { PageIcon } from './PageIcon';

const HOME = ['M3 11l9-8 9 8', 'M5 10v10h14V10'];
const INBOX = ['M22 12h-6l-2 3h-4l-2-3H2', 'M5.5 5h13l3.5 7v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6z'];
const SEARCH = ['M20 20l-4-4'];
const SEARCH_CIRCLES = [{ cx: 11, cy: 11, r: 7 }];
const FILE = ['M6 3h8l4 4v14H6z', 'M14 3v4h4'];
const PLUS = ['M12 5v14', 'M5 12h14'];
const IMPORT = ['M12 4v12', 'M7 11l5 5 5-5', 'M5 20h14'];
const INVITE = [
  'M15 20a6 6 0 0 0-12 0',
  'M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  'M19 8v6',
  'M16 11h6',
];
const TRASH = ['M4 7h16', 'M9 7V4h6v3', 'M6 7l1 13h10l1-13'];
const SETTINGS = ['M4 7h9', 'M17 7h3', 'M4 17h3', 'M11 17h9'];
const SETTINGS_CIRCLES = [
  { cx: 15, cy: 7, r: 2 },
  { cx: 9, cy: 17, r: 2 },
];
const CHEVRON = ['M9 6l6 6-6 6'];

function MenuIcon({
  d,
  circles = [],
  size = 20,
  strokeWidth = 1.75,
}: {
  d: readonly string[];
  circles?: readonly { cx: number; cy: number; r: number }[];
  size?: number;
  strokeWidth?: number;
}): React.ReactElement {
  return (
    <svg
      className="mm-ic"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {d.map((path) => (
        <path key={path} d={path} />
      ))}
      {circles.map((circle) => (
        <circle key={`${circle.cx}-${circle.cy}`} cx={circle.cx} cy={circle.cy} r={circle.r} />
      ))}
    </svg>
  );
}

function pageRows(
  pages: PageSummary[],
  parentId: string | null,
  depth: number,
  activePageId: string | null,
  onOpen: (id: string, newTab: boolean) => void,
): React.ReactElement[] {
  return pages
    .filter((page) => !page.trashedAt && page.parentId === parentId)
    .flatMap((page) => {
      const current = page.id === activePageId;
      return [
        <button
          key={page.id}
          type="button"
          className={current ? 'mm-row is-sub is-active' : 'mm-row is-sub'}
          style={depth > 1 ? { paddingLeft: 32 + (depth - 1) * 16 } : undefined}
          aria-current={current ? 'page' : undefined}
          onClick={(event) => onOpen(page.id, event.metaKey || event.ctrlKey)}
        >
          {page.icon ? (
            <span className="mm-emoji">
              <PageIcon icon={page.icon} />
            </span>
          ) : (
            <MenuIcon d={FILE} />
          )}
          <span className="mm-label">{page.title || t('page.untitled')}</span>
        </button>,
        ...pageRows(pages, page.id, depth + 1, activePageId, onOpen),
      ];
    });
}

export function MobileMenu({
  initials,
  unread,
  favorites,
  workspaces,
  pagesByWorkspace,
  collapsed,
  activePageId,
  onToggleWorkspace,
  onHome,
  onInbox,
  onSearch,
  onOpenPage,
  onImport,
  onInvite,
  onTrash,
  onSettings,
  onNewWorkspace,
  account,
}: {
  initials: string;
  unread: number;
  favorites: PageSummary[];
  workspaces: WorkspaceSummary[];
  pagesByWorkspace: Record<string, PageSummary[]>;
  collapsed: Record<string, boolean>;
  activePageId: string | null;
  onToggleWorkspace: (id: string) => void;
  onHome: () => void;
  onInbox: () => void;
  onSearch: () => void;
  onOpenPage: (id: string, newTab: boolean) => void;
  onImport: () => void;
  onInvite: () => void;
  onTrash: () => void;
  onSettings: () => void;
  onNewWorkspace: () => void;
  account: ReactNode;
}): React.ReactElement {
  return (
    <div className="mm">
      <div className="mm-head">
        <Logo className="mm-logo" height={20} />
      </div>
      <div className="mm-searchwrap">
        <button type="button" className="mm-search" onClick={onSearch}>
          <MenuIcon d={SEARCH} circles={SEARCH_CIRCLES} />
          <span>{t('sidebar.searchPages')}</span>
        </button>
      </div>
      <nav className="mm-nav" aria-label={t('workspace.sidebar')}>
        <div className="mm-group">
          <button type="button" className="mm-row" onClick={onHome}>
            <MenuIcon d={HOME} />
            <span className="mm-label">{t('sidebar.home')}</span>
          </button>
          <button
            type="button"
            className="mm-row"
            aria-label={unread > 0 ? t('sidebar.inboxCount', { count: String(unread) }) : undefined}
            onClick={onInbox}
          >
            <MenuIcon d={INBOX} />
            <span className="mm-label">{t('sidebar.inbox')}</span>
            {unread > 0 ? (
              <span className="mm-badge" aria-hidden="true">
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </button>
        </div>
        {favorites.length > 0 ? (
          <>
            <div className="mm-sectionlabel">{t('sidebar.favorites')}</div>
            <div className="mm-group">
              {favorites.map((page) => {
                const current = page.id === activePageId;
                return (
                  <button
                    key={page.id}
                    type="button"
                    className={current ? 'mm-row is-active' : 'mm-row'}
                    aria-current={current ? 'page' : undefined}
                    onClick={(event) => onOpenPage(page.id, event.metaKey || event.ctrlKey)}
                  >
                    {page.icon ? (
                      <span className="mm-emoji">
                        <PageIcon icon={page.icon} />
                      </span>
                    ) : (
                      <MenuIcon d={FILE} />
                    )}
                    <span className="mm-label">{page.title || t('page.untitled')}</span>
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
        <div className="mm-wslabelrow">
          <div className="mm-sectionlabel">{t('sidebar.workspaces')}</div>
          <button
            type="button"
            className="mm-icobtn mm-addws"
            aria-label={t('sidebar.newWorkspace')}
            onClick={onNewWorkspace}
          >
            <MenuIcon d={PLUS} />
          </button>
        </div>
        <div className="mm-group">
          {workspaces.map((workspace) => {
            const open = !collapsed[workspace.id];
            const pages = pagesByWorkspace[workspace.id] ?? [];
            const listId = `mm-ws-${workspace.id}`;
            return (
              <div className="mm-ws" key={workspace.id}>
                <button
                  type="button"
                  className="mm-row mm-wshead"
                  aria-expanded={open}
                  aria-controls={listId}
                  onClick={() => onToggleWorkspace(workspace.id)}
                >
                  <span className="mm-wsav">
                    {(workspace.name || '?').slice(0, 1).toUpperCase()}
                  </span>
                  <span className="mm-label mm-wsname">{workspace.name}</span>
                  <span className="mm-chev">
                    <MenuIcon d={CHEVRON} size={16} strokeWidth={2} />
                  </span>
                </button>
                {open ? (
                  <div className="mm-wspages" id={listId}>
                    {pageRows(pages, null, 1, activePageId, onOpenPage)}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="mm-rule" />
        <div className="mm-group">
          <button type="button" className="mm-row" onClick={onImport}>
            <MenuIcon d={IMPORT} />
            <span className="mm-label">{t('sidebar.import')}</span>
          </button>
          <button type="button" className="mm-row" onClick={onInvite}>
            <MenuIcon d={INVITE} />
            <span className="mm-label">{t('sidebar.inviteMembers')}</span>
          </button>
        </div>
      </nav>
      <div className="mm-foot">
        <div className="mm-user">
          <span className="mm-avatar" aria-hidden="true">
            {initials}
          </span>
          {account}
          <button
            type="button"
            className="mm-icobtn"
            aria-label={t('sidebar.trash')}
            onClick={onTrash}
          >
            <MenuIcon d={TRASH} />
          </button>
          <button
            type="button"
            className="mm-icobtn"
            aria-label={t('sidebar.settings')}
            onClick={onSettings}
          >
            <MenuIcon d={SETTINGS} circles={SETTINGS_CIRCLES} />
          </button>
        </div>
      </div>
    </div>
  );
}
